import { useGetLocale, useTranslate } from "@refinedev/core";

import { formatDate } from "../../i18n/format";
import { COMPONENT_PER_SLEUTEL } from "./dierComponenten";
import { DierOnderdeel } from "./DierOnderdeel";
import { statusVanEntingen, statusVanGeldigheid } from "./dierStatus";
import type { PensionStand } from "../pensionKlantTypes";

/**
 * De vier medische onderdelen van een samengestelde diertegel: entingen, ontworming, ontvlooiing en
 * medicatie.
 *
 * <p>Ze lezen alle vier uit dezelfde bron: de {@code Stand} die het pensiondierscherm toch al ophaalt.
 * Vier aparte bevragingen zou vier keer hetzelfde werk zijn voor gegevens die de server al bij elkaar
 * heeft gezet -- en dan kunnen ze ook nog uit elkaar lopen wanneer de een net ververst is en de ander
 * niet.
 *
 * <p><b>Nog zonder snelle actie.</b> Het ontwerp wil een plus bij elk van deze vier. Voor een enting
 * ligt vast waar die heen gaat; voor een toediening, een ontworming of een ontvlooiing niet: die hangen
 * aan een voorschrift of een product, en welk van de twee is een keuze die het pension maakt en niet
 * ik. Zolang dat open staat tonen deze onderdelen wat er is zonder knop -- een knop die naar het
 * verkeerde voorschrift schrijft is erger dan geen knop.
 */

export function EntingenOnderdeel({ stand, vandaag = new Date() }: {
  stand: PensionStand | null;
  vandaag?: Date;
}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const verlopen = stand?.entingenVerlopen ?? 0;
  const geldigTot = stand?.entingGeldigTot ?? null;

  return (
    <DierOnderdeel
      def={COMPONENT_PER_SLEUTEL.vaccinaties}
      waarde={
        verlopen > 0
          ? t("dierTegel.entingen.verlopen", { args: [String(verlopen)] })
          : geldigTot
            ? t("dierTegel.entingen.geldigTot", { args: [formatDate(geldigTot, locale)] })
            : null
      }
      // De eerste die afloopt is het eerste waar iemand iets aan moet doen; de rest volgt later.
      herkomst={stand?.entingVerlooptOp && verlopen === 0
        ? t("dierTegel.entingen.eersteAfloop", { args: [formatDate(stand.entingVerlooptOp, locale)] })
        : undefined}
      status={statusVanEntingen(verlopen, geldigTot, vandaag)}
    />
  );
}

export function OntwormingOnderdeel({ stand, vandaag = new Date() }: {
  stand: PensionStand | null;
  vandaag?: Date;
}) {
  const locale = useGetLocale()() ?? "nl";
  const t = useTranslate();
  return (
    <DierOnderdeel
      def={COMPONENT_PER_SLEUTEL.ontworming}
      waarde={stand?.ontwormdOp ? formatDate(stand.ontwormdOp, locale) : null}
      herkomst={stand?.ontwormingGeldigTot
        ? t("dierTegel.behandeling.volgende", { args: [formatDate(stand.ontwormingGeldigTot, locale)] })
        : undefined}
      status={statusVanGeldigheid(stand?.ontwormingGeldigTot, vandaag)}
    />
  );
}

export function OntvlooiingOnderdeel({ stand, vandaag = new Date() }: {
  stand: PensionStand | null;
  vandaag?: Date;
}) {
  const locale = useGetLocale()() ?? "nl";
  const t = useTranslate();
  return (
    <DierOnderdeel
      def={COMPONENT_PER_SLEUTEL.ontvlooiing}
      waarde={stand?.ontvlooidOp ? formatDate(stand.ontvlooidOp, locale) : null}
      herkomst={stand?.ontvlooiingGeldigTot
        ? t("dierTegel.behandeling.volgende", { args: [formatDate(stand.ontvlooiingGeldigTot, locale)] })
        : undefined}
      status={statusVanGeldigheid(stand?.ontvlooiingGeldigTot, vandaag)}
    />
  );
}

/**
 * Medicatie: welk middel er loopt, en hoeveel er in totaal lopen.
 *
 * <p>Geen status: een lopend middel is niet "in orde" of "verlopen" maar gewoon een gegeven. Er een
 * kleur aan hangen zou beweren dat medicatie iets is dat goed of fout staat, en dat is aan de
 * dierenarts.
 */
export function MedicatieOnderdeel({ stand }: { stand: PensionStand | null }) {
  const t = useTranslate();
  const aantal = stand?.medicatieActief ?? 0;
  const naam = stand?.medicatieNaam ?? null;

  return (
    <DierOnderdeel
      def={COMPONENT_PER_SLEUTEL.medicatie}
      waarde={aantal === 0 ? null : naam ?? t("dierTegel.medicatie.aantal", { args: [String(aantal)] })}
      herkomst={aantal > 1 ? t("dierTegel.medicatie.meer", { args: [String(aantal - 1)] }) : undefined}
    />
  );
}
